import { useEffect, useState } from 'react';
import { Check, X, ExternalLink, ShoppingCart, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

const SERVICE_NAMES: Record<string, { name: string; icon: string }> = {
  'sign-forecast':    { name: 'Previsão do Signo',            icon: '🌟' },
  'sign-synastry':    { name: 'Sinastria entre Signos',       icon: '♾️' },
  'birth-chart':      { name: 'Mapa Astral',                  icon: '🌌' },
  'forecast-daily':   { name: 'Previsão Diária',              icon: '🔭' },
  'forecast-weekly':  { name: 'Previsão Semanal',             icon: '🔭' },
  'forecast-monthly': { name: 'Previsão Mensal',              icon: '🔭' },
  'forecast-annual':  { name: 'Previsão Anual',               icon: '🔭' },
  'synastry':         { name: 'Sinastria',                    icon: '💫' },
  'astromatch':       { name: 'AstroMatch',                   icon: '💘' },
  'astrocartography': { name: 'Astrocartografia',             icon: '🌍' },
  'questions':        { name: 'Perguntar aos Astros',         icon: '🔮' },
  'tarot-single':     { name: 'Tarot: Carta do Dia',          icon: '🃏' },
  'tarot-three':      { name: 'Tarot: Pass. · Pres. · Fut.', icon: '🃏' },
  'tarot-horseshoe':  { name: 'Tarot: Ferradura',             icon: '🃏' },
  'tarot-celtic':     { name: 'Tarot: Cruz Celta',            icon: '🃏' },
  'lenormand-single': { name: 'Cigano: Carta do Momento',     icon: '🎴' },
  'lenormand-three':  { name: 'Cigano: Linha do Tempo',       icon: '🎴' },
  'lenormand-five':   { name: 'Cigano: Cruz',                 icon: '🎴' },
  'lenormand-nine':   { name: 'Cigano: Quadrado',             icon: '🎴' },
  'i-ching':          { name: 'I Ching',                      icon: '☯️' },
  'numerology':       { name: 'Numerologia',                  icon: '🔢' },
  'vedic-astrology':  { name: 'Astrologia Védica',            icon: '✨' },
  'human-design':     { name: 'Design Humano',                icon: '🧬' },
  'agenda':           { name: 'Agenda Astrológica',           icon: '📅' },
  'moon-phases':      { name: 'Fases da Lua',                 icon: '🌙' },
  'retrogrado':       { name: 'Radar Retrógrado',             icon: '↩️' },
  'eclipses':         { name: 'Eclipse Insight',              icon: '🌑' },
  'astro-coach':      { name: 'Astro Coach',                  icon: '🎓' },
  'rota-vocacional':  { name: 'Rota Profissional: Vocacional', icon: '🚀' },
  'rota-transition':  { name: 'Rota Profissional: Transição', icon: '🚀' },
  'astro-pet':        { name: 'Astro Pet',                    icon: '🐾' },
  'dream-journal':    { name: 'Diário dos Sonhos',            icon: '📔' },
};

interface Product {
  id: string;
  name: string;
  description: string;
  price_brl: number;
  price_usd: number;
  credits: number | null;
  category: string;
  active: boolean;
  stripe_price_id_brl?: string;
}

interface AuditEntry { created_at: string; admin_email: string; }

const CATEGORIES = [
  { key: 'subscription', label: 'Planos' },
  { key: 'questions',    label: 'Perguntas' },
  { key: 'astromatch',   label: 'AstroMatch' },
  { key: 'synastry',     label: 'Sinastria' },
];

function StatusDot({ product }: { product: Product }) {
  if (product.price_brl === 0) return <span title="Gratuito, acessível sem pagamento">🟡</span>;
  if (product.stripe_price_id_brl?.startsWith('price_')) return <span title="Pago + Stripe configurado, pronto para vender">🟢</span>;
  return <span title="Pago mas sem Stripe Price ID, não está à venda!">🔴</span>;
}

/** Tooltip wrapper for "Stripe Price ID" label */
function StripePriceLabel() {
  return (
    <span
      title="Stripe Price ID: código único criado no painel da Stripe para cada produto/preço. Formato: price_xxxx... Sem ele, o botão de compra não funcionará."
      style={{ borderBottom: '1px dotted var(--text-muted)', cursor: 'help' }}
    >
      Stripe Price ID (BRL)
    </span>
  );
}

function ProductRow({ product, onSaved }: { product: Product; onSaved: () => void }) {
  const { profile }    = useAuth();
  const { showToast }  = useToast();
  const [editing,   setEditing]   = useState(false);
  const [priceBrl,  setPriceBrl]  = useState(String(product.price_brl));
  const [isFree,    setIsFree]    = useState(product.price_brl === 0);
  const [stripePid, setStripePid] = useState(product.stripe_price_id_brl ?? '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [auditInfo, setAuditInfo] = useState<AuditEntry | null>(null);

  useEffect(() => {
    if (!editing) loadAudit();
  }, [editing]);

  async function loadAudit() {
    try {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select('created_at, admin_id')
        .eq('entity_id', product.id)
        .eq('action', 'update_price')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !data) return;
      setAuditInfo({ created_at: data.created_at, admin_email: 'admin' });
    } catch { /* audit info optional */ }
  }

  async function save() {
    setSaving(true);
    setError(null);
    const newPrice = isFree ? 0 : parseFloat(priceBrl.replace(',', '.'));
    if (isNaN(newPrice) || newPrice < 0) { setError('Preço inválido'); setSaving(false); return; }
    if (!isFree && stripePid && (!stripePid.startsWith('price_') || stripePid.length < 25)) {
      setError('Stripe Price ID inválido (deve começar com price_ e ter ≥25 caracteres)');
      setSaving(false); return;
    }

    const oldValue = { price_brl: product.price_brl, stripe_price_id_brl: product.stripe_price_id_brl };
    const newValue = { price_brl: newPrice, stripe_price_id_brl: stripePid || null };

    const { error: updErr } = await supabase
      .from('product_prices')
      .update({ price_brl: newPrice, stripe_price_id_brl: stripePid || null })
      .eq('id', product.id);

    if (updErr) {
      setError(updErr.message);
      showToast({ message: `Erro ao salvar: ${updErr.message}`, type: 'error' });
      setSaving(false);
      return;
    }

    await supabase.from('admin_audit_log').insert({
      admin_id:    profile?.id,
      action:      'update_price',
      entity_type: 'product_price',
      entity_id:   product.id,
      old_value:   oldValue,
      new_value:   newValue,
    });

    showToast({ message: `Preço de "${product.name}" atualizado ✦`, type: 'success' });
    setSaving(false);
    setEditing(false);
    onSaved();
  }

  const previewPrice = isFree ? 'Acessar Gratuitamente' : `Comprar, R$ ${parseFloat(priceBrl.replace(',', '.') || '0').toFixed(2)}`;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 0', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
        <StatusDot product={product} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.1rem', fontSize: 'var(--text-label)' }}>{product.name}</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{product.id}</p>
        </div>
        <span style={{ fontFamily: 'var(--font-display)', color: 'var(--petrol)', fontWeight: 700 }}>
          R$ {product.price_brl.toFixed(2)}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(e => !e)}>
          {editing ? 'Cancelar' : 'Editar'}
        </button>
      </div>

      {editing && (
        <div className="animate-fade-in" style={{ padding: '1rem', backgroundColor: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', margin: '0.5rem 0 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Free toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={isFree} onChange={e => setIsFree(e.target.checked)} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
            Gratuito (preço zero)
          </label>

          {!isFree && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">Preço BRL</label>
                  <input type="text" className="form-input" value={priceBrl} onChange={e => setPriceBrl(e.target.value)} placeholder="24,90" />
                </div>
                <div style={{ paddingTop: '1.5rem', color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                  = R$ {parseFloat(priceBrl.replace(',', '.') || '0').toFixed(2)}
                </div>
              </div>

              <div>
                <label className="form-label"><StripePriceLabel /></label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="text" className="form-input" value={stripePid} onChange={e => setStripePid(e.target.value)} placeholder="price_1abc..." style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px' }} />
                  {stripePid && (
                    <a href={`https://dashboard.stripe.com/prices/${stripePid}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                {stripePid && stripePid.startsWith('price_') && stripePid.length >= 25
                  ? <p style={{ fontSize: '11px', color: '#4ade80', marginTop: '0.25rem' }}>✓ Formato válido</p>
                  : stripePid && <p style={{ fontSize: '11px', color: '#fca5a5', marginTop: '0.25rem' }}>Formato inválido</p>}
              </div>
            </>
          )}

          {/* BuyButton preview */}
          <div>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Pré-visualização para usuários:</p>
            <div style={{ padding: '0.5rem 1rem', backgroundColor: isFree ? 'var(--bg-surface)' : 'var(--petrol)', color: isFree ? 'var(--petrol)' : '#0F1123', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-label)', fontWeight: 600, display: 'inline-block' }}>
              ✦ {previewPrice}
            </div>
          </div>

          {error && <p style={{ color: '#fca5a5', fontSize: 'var(--text-caption)' }}>{error}</p>}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Check size={14} /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {auditInfo && (
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0.25rem 0 0.75rem', fontStyle: 'italic' }}>
          Última alteração: {new Date(auditInfo.created_at).toLocaleDateString('pt-BR')} por {auditInfo.admin_email}
        </p>
      )}
    </div>
  );
}

function AvulsoProductsSection() {
  const { showToast } = useToast();
  type AvulsoEntry = { enabled: boolean; price_brl: number; price_usd?: number };
  const [matrix,  setMatrix]  = useState<Record<string, AvulsoEntry>>({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', 'plan_access_avulso_pricing').maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try {
            const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
            if (parsed && typeof parsed === 'object') setMatrix(parsed as Record<string, AvulsoEntry>);
          } catch { /* ignore */ }
        }
        setLoading(false);
      });
  }, []);

  const enabledIds = Object.entries(matrix).filter(([, v]) => v.enabled).map(([id]) => id);

  async function save() {
    setSaving(true);
    const { error } = await supabase.from('platform_settings').upsert({
      key: 'plan_access_avulso_pricing',
      value: JSON.stringify(matrix),
      description: 'Preços avulsos por serviço (one-off purchase)',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'key' });
    setSaving(false);
    if (error) showToast({ type: 'error', message: 'Erro ao salvar preços avulsos' });
    else showToast({ type: 'success', message: 'Preços avulsos salvos!' });
  }

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>Carregando...</div>;

  if (enabledIds.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <ShoppingCart size={14} style={{ color: '#c084fc' }} />
          <p style={{ fontSize: 'var(--text-caption)', color: '#c084fc', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Produtos Avulsos
          </p>
        </div>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          Nenhum serviço marcado como avulso ainda. Ative a coluna "Avulso" em <a href="/admin-ap/plan-access" style={{ color: 'var(--petrol)' }}>Acesso por Plano</a> para liberar a compra individual.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ShoppingCart size={14} style={{ color: '#c084fc' }} />
          <p style={{ fontSize: 'var(--text-caption)', color: '#c084fc', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Produtos Avulsos
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <Save size={13} /> {saving ? 'Salvando...' : 'Salvar preços'}
        </button>
      </div>
      <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        Serviços ativados como avulso em "Acesso por Plano". Defina o preço de compra individual aqui.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {enabledIds.map(id => {
          const info = SERVICE_NAMES[id] ?? { name: id, icon: '🛒' };
          const entry = matrix[id];
          return (
            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.6rem 0', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.1rem' }}>{info.icon}</span>
              <span style={{ flex: 1, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 500 }}>{info.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>R$</span>
                <input
                  type="number" min={0} step={0.01} placeholder="0,00"
                  value={entry.price_brl || ''}
                  onChange={e => setMatrix(prev => ({
                    ...prev,
                    [id]: { ...prev[id], price_brl: parseFloat(e.target.value) || 0 },
                  }))}
                  style={{ width: '80px', textAlign: 'right', fontSize: 'var(--text-label)', padding: '4px 8px',
                    border: '1px solid rgba(192,132,252,0.5)', borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-input)', color: '#c084fc' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminPricingPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await supabase.from('product_prices').select('*').order('category').order('price_brl');
    if (data) setProducts(data);
    setLoading(false);
  }

  if (loading) return <div className="card shimmer" style={{ height: '400px' }} />;

  return (
    <div>
      <AdminPageHeader
        title="Preços e Produtos"
        objective="Gerencie os preços de cada serviço. Alterações aqui afetam imediatamente o que os usuários veem na página de planos. Stripe Price ID é o código interno da Stripe que autoriza a cobrança, sem ele o produto pago não funciona."
      />

      <AvulsoProductsSection />

      {CATEGORIES.map(cat => {
        const catProducts = products.filter(p => p.category === cat.key);
        if (!catProducts.length) return null;
        return (
          <div key={cat.key} className="card" style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>
              {cat.label}
            </p>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.75rem', fontStyle: 'italic' }}>
              {catProducts.length} {catProducts.length === 1 ? 'produto' : 'produtos'} nesta categoria
            </p>
            {catProducts.map(p => <ProductRow key={p.id} product={p} onSaved={load} />)}
          </div>
        );
      })}
    </div>
  );
}
